import { useState } from 'react';
import { sendPushToAll, sendPushToUser } from '../services/pushService';
import { useAuthStore } from '../stores/authStore';
import UserFinder from '../components/common/UserFinder';
import type { User } from '../types';

export default function PushPage() {
  const { adminUser } = useAuthStore();
  const [tab, setTab] = useState<'all' | 'user'>('all');

  // All users
  const [allTitle, setAllTitle] = useState('');
  const [allBody, setAllBody] = useState('');
  const [allUrgent, setAllUrgent] = useState(false);
  const [allSending, setAllSending] = useState(false);
  const [allResult, setAllResult] = useState('');

  // Specific user
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [userTitle, setUserTitle] = useState('');
  const [userBody, setUserBody] = useState('');
  const [userUrgent, setUserUrgent] = useState(false);
  const [userSending, setUserSending] = useState(false);
  const [userResult, setUserResult] = useState('');

  async function handleSendAll() {
    if (!allTitle || !allBody || !adminUser) return;
    const urgentText = allUrgent ? '\n\n[긴급 알림] 서버 알림 OFF 사용자에게도 전송됩니다.' : '';
    if (!confirm(`전체 사용자에게 푸시를 전송하시겠습니까?\n\n제목: ${allTitle}\n내용: ${allBody}${urgentText}`)) return;
    setAllSending(true);
    setAllResult('');
    try {
      const res = await sendPushToAll(allTitle, allBody, allUrgent);
      setAllResult(res.success ? `전송 성공! ${res.message}` : `실패: ${res.message}`);
      if (res.success) { setAllTitle(''); setAllBody(''); setAllUrgent(false); }
    } catch (err) {
      setAllResult(`오류: ${err}`);
    } finally { setAllSending(false); }
  }

  async function handleSendUser() {
    if (!targetUser || !userTitle || !userBody || !adminUser) return;
    setUserSending(true);
    setUserResult('');
    try {
      const res = await sendPushToUser(targetUser.nickname, userTitle, userBody, userUrgent);
      setUserResult(res.success ? `전송 성공! ${res.message}` : `실패: ${res.message}`);
      if (res.success) { setTargetUser(null); setUserTitle(''); setUserBody(''); setUserUrgent(false); }
    } catch (err) {
      setUserResult(`오류: ${err}`);
    } finally { setUserSending(false); }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">푸시 알림</h1>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => setTab('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}
        >
          전체 전송
        </button>
        <button
          onClick={() => setTab('user')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'user' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}
        >
          특정 사용자
        </button>
      </div>

      {tab === 'all' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-lg">
          <h2 className="font-semibold mb-4">전체 사용자에게 푸시 전송</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-500 mb-1">제목</label>
              <input value={allTitle} onChange={e => setAllTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">내용</label>
              <textarea value={allBody} onChange={e => setAllBody(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={allUrgent} onChange={e => setAllUrgent(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
              <span className="text-sm text-red-600 font-medium">긴급 알림</span>
              <span className="text-xs text-gray-400">(서버 알림 OFF 사용자에게도 전송)</span>
            </label>
            {allResult && <p className={`text-sm ${allResult.includes('성공') ? 'text-green-600' : 'text-red-600'}`}>{allResult}</p>}
            <button onClick={handleSendAll} disabled={allSending} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
              {allSending ? '전송 중...' : '전체 전송'}
            </button>
          </div>
        </div>
      )}

      {tab === 'user' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-lg">
          <h2 className="font-semibold mb-4">특정 사용자에게 푸시 전송</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-500 mb-1">대상 사용자</label>
              <UserFinder onUserSelect={setTargetUser} labelWidth="w-16" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">제목</label>
              <input value={userTitle} onChange={e => setUserTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">내용</label>
              <textarea value={userBody} onChange={e => setUserBody(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={userUrgent} onChange={e => setUserUrgent(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
              <span className="text-sm text-red-600 font-medium">긴급 알림</span>
              <span className="text-xs text-gray-400">(서버 알림 OFF여도 전송)</span>
            </label>
            {userResult && <p className={`text-sm ${userResult.includes('성공') ? 'text-green-600' : 'text-red-600'}`}>{userResult}</p>}
            <button onClick={handleSendUser} disabled={userSending || !targetUser} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
              {userSending ? '전송 중...' : '전송'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
